/**
 * Sidecar Turn Queue
 *
 * Shared helpers for preserving follow-up turns while Sidecar is running.
 *
 * Primary responsibilities:
 * - Define queued Sidecar turn state
 * - Keep only send options that can safely be replayed
 *
 * Dependencies:
 * - Chat send option types
 */

import {
  cloneQueuedAttachments,
  mergeQueuedAttachments
} from "@/lib/chat/queued-attachments"
import type { ChatSendOptions } from "@/types/chat"

export interface QueuedSidecarTurn {
  id: string
  message: string
  options?: ChatSendOptions
  queuedAfterRunId?: string | null
}

export interface QueuedSidecarTurnReplayBatch {
  turns: QueuedSidecarTurn[]
  message: string
  options?: ChatSendOptions
}

type BuildQueuedSidecarTurnOptions = (
  turn: QueuedSidecarTurn
) => ChatSendOptions | undefined

function cloneStructuredMentions(
  mentions: ChatSendOptions["structuredMentions"]
) {
  if (!mentions) return undefined

  return {
    items: mentions.items.map(item => ({ ...item })),
    memberUserIds: [...mentions.memberUserIds],
    assistantIds: [...mentions.assistantIds],
    platformLosiMentioned: mentions.platformLosiMentioned,
    ...(mentions.everyoneMentioned ? { everyoneMentioned: true } : {})
  }
}

function getUniqueStrings(values: string[]) {
  return Array.from(new Set(values))
}

function getOrderedQueuedTurns(
  turns: QueuedSidecarTurn[],
  requestedTurnId?: string | null
) {
  if (!requestedTurnId) return turns

  const requestedTurn = turns.find(turn => turn.id === requestedTurnId)

  if (!requestedTurn) return turns

  return [
    requestedTurn,
    ...turns.filter(turn => turn.id !== requestedTurnId)
  ]
}

function getQueuedTurnReplayMessage(turns: QueuedSidecarTurn[]) {
  return turns
    .map(turn => turn.message.trim())
    .filter(Boolean)
    .join("\n\n")
}

function mergeStructuredMentions(options: ChatSendOptions[]) {
  const mentions = options
    .map(option => option.structuredMentions)
    .filter(
      (mention): mention is NonNullable<ChatSendOptions["structuredMentions"]> =>
        Boolean(mention)
    )

  if (mentions.length === 0) return undefined

  const items = new Map<string, NonNullable<
    ChatSendOptions["structuredMentions"]
  >["items"][number]>()

  for (const mention of mentions) {
    for (const item of mention.items) {
      const key = `${item.kind}:${item.type}:${item.id}:${item.label}`

      if (!items.has(key)) {
        items.set(key, { ...item })
      }
    }
  }

  return {
    items: Array.from(items.values()),
    memberUserIds: getUniqueStrings(
      mentions.flatMap(mention => mention.memberUserIds)
    ),
    assistantIds: getUniqueStrings(
      mentions.flatMap(mention => mention.assistantIds)
    ),
    platformLosiMentioned: mentions.some(mention => mention.platformLosiMentioned),
    ...(mentions.some(mention => mention.everyoneMentioned)
      ? { everyoneMentioned: true }
      : {})
  }
}

function mergeBooleanReadiness(
  options: ChatSendOptions[]
): boolean | undefined {
  const readinessValues = options
    .map(option => option.groupMemberMentionTokensReady)
    .filter((value): value is boolean => typeof value === "boolean")

  if (readinessValues.length === 0) return undefined

  return readinessValues.every(Boolean)
}

function mergeQueuedTurnOptions(
  options: Array<ChatSendOptions | undefined>
): ChatSendOptions | undefined {
  const presentOptions = options.filter(
    (option): option is ChatSendOptions => Boolean(option)
  )

  if (presentOptions.length === 0) return undefined

  const queuedAttachments = presentOptions.some(option => option.queuedAttachments)
    ? mergeQueuedAttachments(
        ...presentOptions.map(option => option.queuedAttachments)
      )
    : undefined
  const desktopHandoffAttachments = presentOptions.flatMap(option =>
    option.desktopHandoffAttachments?.map(attachment => ({ ...attachment })) ?? []
  )
  const groupMemberMentionTokens = getUniqueStrings(
    presentOptions.flatMap(option => option.groupMemberMentionTokens ?? [])
  )
  const structuredMentions = mergeStructuredMentions(presentOptions)
  const groupMemberMentionTokensReady = mergeBooleanReadiness(presentOptions)
  const addon = presentOptions.find(option => option.addon)?.addon
  const sidecar = presentOptions.find(option => option.sidecar)?.sidecar
  const voiceMemo = presentOptions.find(option => option.voiceMemo)?.voiceMemo

  return {
    ...(addon ? { addon } : {}),
    ...(presentOptions.some(option => option.forceNewChat)
      ? { forceNewChat: true }
      : {}),
    ...(presentOptions.some(option => option.hasAttachments) || queuedAttachments
      ? { hasAttachments: true }
      : {}),
    ...(queuedAttachments ? { queuedAttachments } : {}),
    ...(desktopHandoffAttachments.length
      ? { desktopHandoffAttachments }
      : {}),
    forceAssistantResponse: true,
    ...(sidecar ? { sidecar: { ...sidecar } } : {}),
    ...(structuredMentions ? { structuredMentions } : {}),
    ...(groupMemberMentionTokens.length
      ? { groupMemberMentionTokens }
      : {}),
    ...(typeof groupMemberMentionTokensReady === "boolean"
      ? { groupMemberMentionTokensReady }
      : {}),
    ...(voiceMemo ? { voiceMemo: { ...voiceMemo } } : {})
  }
}

export function getQueueableChatSendOptions(
  options?: ChatSendOptions
): ChatSendOptions | undefined {
  if (!options) return undefined

  return {
    ...(options.addon ? { addon: options.addon } : {}),
    ...(options.forceNewChat ? { forceNewChat: true } : {}),
    ...(options.forceAssistantResponse ? { forceAssistantResponse: true } : {}),
    ...(typeof options.hasAttachments === "boolean"
      ? { hasAttachments: options.hasAttachments }
      : {}),
    ...(options.queuedAttachments
      ? { queuedAttachments: cloneQueuedAttachments(options.queuedAttachments) }
      : {}),
    ...(options.desktopHandoffAttachments?.length
      ? {
          desktopHandoffAttachments: options.desktopHandoffAttachments.map(
            attachment => ({ ...attachment })
          )
        }
      : {}),
    ...(options.sidecar ? { sidecar: { ...options.sidecar } } : {}),
    ...(options.structuredMentions
      ? { structuredMentions: cloneStructuredMentions(options.structuredMentions) }
      : {}),
    ...(options.groupMemberMentionTokens
      ? { groupMemberMentionTokens: [...options.groupMemberMentionTokens] }
      : {}),
    ...(typeof options.groupMemberMentionTokensReady === "boolean"
      ? { groupMemberMentionTokensReady: options.groupMemberMentionTokensReady }
      : {}),
    ...(options.voiceMemo ? { voiceMemo: { ...options.voiceMemo } } : {})
  }
}

export function getQueuedSidecarTurnReplayBatch(
  turns: QueuedSidecarTurn[],
  requestedTurnId: string | null | undefined,
  buildOptions: BuildQueuedSidecarTurnOptions
): QueuedSidecarTurnReplayBatch | null {
  const orderedTurns = getOrderedQueuedTurns(turns, requestedTurnId)

  if (orderedTurns.length === 0) return null

  return {
    turns: orderedTurns,
    message: getQueuedTurnReplayMessage(orderedTurns),
    options: mergeQueuedTurnOptions(orderedTurns.map(buildOptions))
  }
}
